import { type FormEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { useAuth, usePortalSession } from '../auth';
import {
  confirmPortalGuestLogin,
  PortalApiError,
  startPortalSignIn,
} from '../services/portalApi';

export function LoginPage() {
  const navigate = useNavigate();
  const { signIn, trySilentSignIn } = useAuth();
  const portalSession = usePortalSession();
  const silentAttempted = useRef(false);
  const [email, setEmail] = useState(
    () => new URLSearchParams(window.location.search).get('email') ?? '',
  );
  const [challengeId, setChallengeId] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  useEffect(() => {
    if (portalSession.status === 'ready') {
      void navigate('/portal', { replace: true });
      return;
    }
    if (portalSession.status !== 'signedOut' || silentAttempted.current) return;

    silentAttempted.current = true;
    void trySilentSignIn().catch(() => {
      // No existing Microsoft session. The shared form remains visible.
    });
  }, [navigate, portalSession.status, trySilentSignIn]);

  async function continueSignIn(event: FormEvent): Promise<void> {
    event.preventDefault();
    setIsBusy(true);
    setMessage(null);
    try {
      const response = await startPortalSignIn(email);
      if (response.signIn.Method === 'Entra') {
        await signIn(response.signIn.Email);
        return;
      }
      setEmail(response.signIn.Email);
      setChallengeId(response.signIn.ChallengeId ?? '');
      setSmsCode('');
      setMessage('A six-digit login code was sent to your mobile phone.');
    } catch (error) {
      setMessage(toMessage(error));
    } finally {
      setIsBusy(false);
    }
  }

  async function verifyCode(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!challengeId || smsCode.length !== 6) return;
    setIsBusy(true);
    setMessage(null);
    try {
      await confirmPortalGuestLogin({ email, challengeId, smsCode });
      portalSession.refresh();
      void navigate('/portal', { replace: true });
    } catch (error) {
      setMessage(toMessage(error));
    } finally {
      setIsBusy(false);
    }
  }

  async function resendCode(): Promise<void> {
    setIsBusy(true);
    setMessage(null);
    try {
      const response = await startPortalSignIn(email);
      if (response.signIn.Method !== 'Guest') {
        setChallengeId('');
        await signIn(response.signIn.Email);
        return;
      }
      setChallengeId(response.signIn.ChallengeId ?? '');
      setSmsCode('');
      setMessage('A new login code was sent to your mobile phone.');
    } catch (error) {
      setMessage(toMessage(error));
    } finally {
      setIsBusy(false);
    }
  }

  return (
    <section className="login-page">
      <div className="login-card">
        <p className="eyebrow">YWAM Norway calling portal</p>
        <h1>Sign in</h1>
        {challengeId ? (
          <>
            <p>
              Enter the code sent by SMS for <strong>{email}</strong>.
            </p>
            <form className="guest-acceptance-form"
              onSubmit={(event) => void verifyCode(event)}>
              <label>
                Code from SMS
                <input required type="text" inputMode="numeric"
                  autoComplete="one-time-code" pattern="[0-9]{6}"
                  maxLength={6} placeholder="000000" value={smsCode}
                  onChange={(event) => setSmsCode(
                    event.target.value.replace(/\D/g, '').slice(0, 6),
                  )} />
              </label>
              <button className="primary-button" type="submit"
                disabled={isBusy || smsCode.length !== 6}>
                {isBusy ? 'Signing in…' : 'Sign in'}
              </button>
              <div className="login-secondary-actions">
                <button className="table-action-button" type="button"
                  disabled={isBusy} onClick={() => void resendCode()}>
                  Send a new code
                </button>
                <button className="table-action-button" type="button"
                  disabled={isBusy} onClick={() => {
                    setChallengeId('');
                    setSmsCode('');
                    setMessage(null);
                  }}>
                  Use another email
                </button>
              </div>
            </form>
          </>
        ) : (
          <>
            <p>
              Enter your email address. Organization members continue with
              Microsoft; external participants receive a code by SMS.
            </p>
            <form className="login-form"
              onSubmit={(event) => void continueSignIn(event)}>
              <label>
                Email
                <input required type="email" autoComplete="email"
                  value={email} placeholder="name@example.org"
                  onChange={(event) => setEmail(event.target.value)} />
              </label>
              <button className="primary-button" type="submit" disabled={isBusy}>
                {isBusy ? 'Continuing…' : 'Continue'}
              </button>
            </form>
          </>
        )}
        {portalSession.status === 'loading' ? (
          <p className="login-status">Checking your existing session…</p>
        ) : null}
        {message ? <p className="api-message" role="alert">{message}</p> : null}
      </div>
    </section>
  );
}

function toMessage(error: unknown): string {
  if (error instanceof PortalApiError || error instanceof Error) return error.message;
  return 'Sign-in could not be completed. Please try again.';
}
